/**
 * Token Store - Caches Deepgram tokens with expiration
 * Persists to localStorage across page reloads
 */

import type { TokenResponse } from '@refinevoice/shared';
import { create } from 'zustand';

interface TokenCache {
  token: string;
  expiresAt: string;
  fetchedAt: number;
}

interface TokenStore {
  cachedToken: TokenCache | null;

  /**
   * Set cached token with expiration
   */
  setCachedToken: (tokenResponse: TokenResponse) => void;

  /**
   * Get cached token if still valid (with 1 minute buffer before expiration)
   * Returns null if expired or not cached
   */
  getCachedToken: () => string | null;

  /**
   * Clear cached token
   */
  clearCachedToken: () => void;

  /**
   * Check if token is still valid
   */
  isTokenValid: () => boolean;
}

const TOKEN_BUFFER_MS = 60 * 1000; // 1 minute buffer before expiration
const STORAGE_KEY = 'refinevoice_token_cache';

// Load initial token from localStorage
function loadInitialToken(): TokenCache | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed: TokenCache = JSON.parse(stored);

    // Validate token hasn't expired
    const expiresAt = new Date(parsed.expiresAt).getTime();
    const now = Date.now();

    if (now + TOKEN_BUFFER_MS >= expiresAt) {
      // Token expired, clear it
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Failed to load cached token from localStorage:', error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

// Save token to localStorage
function saveTokenToStorage(tokenCache: TokenCache): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokenCache));
  } catch (error) {
    console.error('Failed to save token to localStorage:', error);
  }
}

// Clear token from localStorage
function clearTokenFromStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear token from localStorage:', error);
  }
}

export const useTokenStore = create<TokenStore>((set, get) => ({
  cachedToken: loadInitialToken(),

  setCachedToken: (tokenResponse: TokenResponse) => {
    const tokenCache: TokenCache = {
      token: tokenResponse.token,
      expiresAt: tokenResponse.expiresAt,
      fetchedAt: Date.now(),
    };

    set({ cachedToken: tokenCache });
    saveTokenToStorage(tokenCache);
  },

  getCachedToken: () => {
    const { cachedToken } = get();

    if (!cachedToken) {
      return null;
    }

    // Check if token is still valid with buffer
    const expiresAt = new Date(cachedToken.expiresAt).getTime();
    const now = Date.now();

    if (now + TOKEN_BUFFER_MS >= expiresAt) {
      // Token expired or will expire soon, clear it
      set({ cachedToken: null });
      clearTokenFromStorage();
      return null;
    }

    return cachedToken.token;
  },

  clearCachedToken: () => {
    set({ cachedToken: null });
    clearTokenFromStorage();
  },

  isTokenValid: () => {
    const { cachedToken } = get();

    if (!cachedToken) {
      return false;
    }

    const expiresAt = new Date(cachedToken.expiresAt).getTime();
    const now = Date.now();

    return now + TOKEN_BUFFER_MS < expiresAt;
  },
}));
